import { IconButton, Typography } from '@mui/joy';
import { Kanban, Lightning, Plus, X } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { ProcessManagement } from './ProcessManagement';
import { ProcessTemplates } from './ProcessTemplates';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { FieldGroup } from '~/components/ui/fieldset';
import { InputField } from '~/components/ui/input';
import { Tab, TabList, TabPanel, Tabs } from '~/components/ui/tabs';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { ProcessService } from '~/services/api/processes/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { useForm } from '~/utils/forms';

const schema = z.object({
  process: z.string(),
  workflowTab: z.string().nullable(),
  selectedTaskId: z.number().nullable(),
  workflowId: z.number().nullable(),
});

type FormFields = z.infer<typeof schema>;
export const useProcessForm = () => useFormContext<FormFields>();

export function ProcessTemplateTab({
  defaultValues,
}: {
  defaultValues: Partial<FormFields>;
}) {
  const form = useForm({
    schema,
    defaultValues: {
      process: '',
      workflowTab: 'new-process',
      selectedTaskId: null,
      workflowId: null,
      ...defaultValues,
    },
  });

  const currentWorkflow = form.watch('workflowTab');

  const { referenceId } = useAppSelector((state) => state.reportData.report);
  const processQuery = ProcessService.endpoints.listReportWorkflows.useQuery({
    params: {
      reference_id: referenceId,
    },
  });

  const changeTab = (tabId: number) => {
    form.setValue('workflowId', tabId);
    form.setValue('workflowTab', `tab-${tabId}`);
  };

  return (
    <FormProvider {...form}>
      <div className='relative'>
        <Tabs value={currentWorkflow}>
          <TabList size='sm' sx={{ overflowX: 'auto' }}>
            <QueryResolver
              query={processQuery}
              errorElement={
                <Tab>
                  <FormattedMessage defaultMessage='Something went Wrong' />
                </Tab>
              }
            >
              {(data) =>
                data.map((item) => (
                  <Tab
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    value={`tab-${item.id}`}
                    key={`${item.id}-${item.name}-tab`}
                    className='!p-0'
                  >
                    <Button
                      variant='plain'
                      color='neutral'
                      size='sm'
                      startDecorator={<Kanban weight='fill' />}
                      className='!bg-transparent'
                      onClick={() => changeTab(Number(item.id))}
                    >
                      {item.name}
                    </Button>
                  </Tab>
                ))
              }
            </QueryResolver>
            <Tab
              value='new-process'
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              <Button
                variant='plain'
                color='neutral'
                size='sm'
                startDecorator={<Plus />}
                className='!bg-transparent'
                onClick={() => {
                  form.setValue('workflowTab', 'new-process');
                  form.setValue('workflowId', null);
                }}
              >
                <FormattedMessage defaultMessage='New Process' />
              </Button>
            </Tab>
          </TabList>
          <QueryResolver query={processQuery}>
            {(data) =>
              data.map((item) => (
                <TabPanel
                  value={`tab-${item.id}`}
                  key={`${item.id}-${item.name}-panel`}
                >
                  <ProcessManagement
                    defaultValues={{
                      id: item.id,
                      name: item.name,
                    }}
                  />
                </TabPanel>
              ))
            }
          </QueryResolver>
          <TabPanel value='new-process'>
            <NewProcess />
          </TabPanel>
        </Tabs>
      </div>
    </FormProvider>
  );
}

export function NewProcess() {
  const form = useProcessForm();

  const process = form.watch('process');

  return (
    <div>
      {process === 'template' && <ProcessTemplates />}

      {process !== 'template' && (
        <div className='flex flex-col justify-center items-center p-4 h-full overflow-auto mt-20 '>
          <Typography level='title-lg'>
            <FormattedMessage defaultMessage='Create new process' />
          </Typography>
          <Typography level='body-sm' color='neutral'>
            <FormattedMessage defaultMessage='Select what you want to create' />
          </Typography>
          <div className='flex gap-2'>
            <ProcessCard type='template' />
            <CreateBlankProcess>
              <ProcessCard type='blank' />
            </CreateBlankProcess>
          </div>
        </div>
      )}
    </div>
  );
}

const ProcessCard = ({ type }: { type: 'template' | 'blank' }) => {
  const intl = useIntl();
  const form = useProcessForm();
  const setProcess = () => form.setValue('process', type);

  const processLabel = {
    template: {
      label: intl.formatMessage({ defaultMessage: 'Use a template' }),
      description: intl.formatMessage({
        defaultMessage: 'Choose from library',
      }),
    },
    blank: {
      label: intl.formatMessage({ defaultMessage: 'Blank process' }),
      description: intl.formatMessage({ defaultMessage: 'Start from scratch' }),
    },
  };

  return (
    <Button
      variant='plain'
      color='neutral'
      className='!p-0'
      onClick={setProcess}
    >
      <Card className='flex flex-col jutify-center items-center !border-none !bg-transparent'>
        <div className='flex flex-row justify-center items-center w-24 aspect-1 border-dashed border-2 rounded-lg'>
          {type == 'template' ? (
            <Lightning size={32} weight='fill' />
          ) : (
            <Plus size={32} />
          )}
        </div>
        <div className='flex flex-col items-center'>
          <Typography level='title-sm'>{processLabel[type].label}</Typography>
          <Typography level='body-xs' color='neutral'>
            {processLabel[type].description}
          </Typography>
        </div>
      </Card>
    </Button>
  );
};

const newFormSchema = z.object({
  isOpen: z.boolean(),
  name: z.string().min(1, 'Process Name cannot be empty'),
});

const CreateBlankProcess = ({ children }: { children: ReactNode }) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const { referenceId } = useAppSelector((state) => state.reportData.report);
  const mainForm = useProcessForm();
  const form = useForm({
    schema: newFormSchema,
    defaultValues: {
      isOpen: false,
      name: '',
    },
  });

  const isFormOpen = form.watch('isOpen');

  const useProcessTemplate = useWrappedMutation(
    ProcessService.endpoints.createReportWorkflow.useMutation(),
  );

  const submitHandler = form.handleSubmit(async (data) => {
    await useProcessTemplate.mutateAsync(
      {
        params: {
          reference_id: referenceId,
        },
        payload: {
          name: data.name,
        },
      },
      {
        onSuccess: (res) => {
          form.setValue('isOpen', !isFormOpen);
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Workflow Created successfully',
            }),
            'success',
          );
          mainForm.setValue('workflowTab', `tab-${res.id}`);
          mainForm.setValue('workflowId', res.id);
        },
        onError: (err) => {
          createToastNotification(getErrorMessage(err), 'error');
        },
      },
    );
    form.reset();
  });

  return (
    <div>
      <Dialog
        open={isFormOpen}
        onOpenChange={() => form.setValue('isOpen', !isFormOpen)}
      >
        <DialogTrigger asChild>
          <Button variant='plain' color='neutral' className='!p-0'>
            {children}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <div className='flex justify-between items-center'>
            <DialogTitle>
              <FormattedMessage defaultMessage='New Process' />
            </DialogTitle>
            <DialogClose asChild>
              <IconButton>
                <X />
              </IconButton>
            </DialogClose>
          </div>
          <DialogBody className='flex flex-col'>
            <form onSubmit={submitHandler}>
              <FieldGroup>
                <InputField
                  control={form.control}
                  field='name'
                  label={intl.formatMessage({ defaultMessage: 'Process Name' })}
                />
                <Button
                  type='submit'
                  variant='solid'
                  color='secondary'
                  disabled={form.formState.isSubmitting}
                  loading={form.formState.isSubmitting}
                  fullWidth
                >
                  <FormattedMessage defaultMessage='Create Process' />
                </Button>
              </FieldGroup>
            </form>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};
